
import React, { useMemo } from 'react';
import { Button } from '@/components/ui/button';
import CalendarDaysHeader from '@/components/CalendarDaysHeader';
import CalendarBody from '@/components/CalendarBody';
import { Event } from '@/lib/types';
import { ScrollArea } from '@/components/ui/scroll-area';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { buildCalendarSlots } from '@/utils/calendarSlots';

interface CalendarViewContentProps {
  days: Date[];
  daysToShow: number;
  cellWidth: number;
  currentDate: Date;
  filteredEvents: Event[];
  navigatePrevious: () => void;
  navigateNext: () => void;
  onEventClick: (event: Event) => void;
}

const CalendarViewContent: React.FC<CalendarViewContentProps> = ({
  days,
  daysToShow,
  cellWidth,
  currentDate,
  filteredEvents,
  navigatePrevious,
  navigateNext,
  onEventClick
}) => {
  const slots = useMemo(
    () => buildCalendarSlots(days, filteredEvents),
    [days, filteredEvents],
  );

  // Rescale cellWidth so the compact slot grid still fills the parent's width
  // (the underlying useCalendar hook sizes cellWidth for the full daysToShow
  // range, so a shorter slot list would otherwise leave a gap on the right).
  const effectiveCellWidth = slots.length > 0
    ? (cellWidth * daysToShow) / slots.length
    : cellWidth;
  return (
    <div>
      <div className="bg-white rounded-b-xl shadow-md overflow-hidden">
        {/* Simple navigation bar with previous/next buttons */}
        <div className="flex justify-between items-center p-4 bg-gradient-to-r from-white to-medihub-softBg border-b border-medihub-primary/10">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={navigatePrevious}
            className="flex items-center gap-1 text-medihub-primary bg-white hover:bg-medihub-lightPurple hover:text-medihub-primary transition-colors duration-200 shadow-sm border-medihub-primary/20"
          >
            <ChevronLeft className="h-4 w-4" />
            <span>Előző</span>
          </Button>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={navigateNext}
            className="flex items-center gap-1 text-medihub-primary bg-white hover:bg-medihub-lightPurple hover:text-medihub-primary transition-colors duration-200 shadow-sm border-medihub-primary/20"
          >
            <span>Következő</span>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
        
        {/* Calendar content with scrolling */}
        <ScrollArea className="w-full">
          <div className="inline-block min-w-full">
            <CalendarDaysHeader
              slots={slots}
              cellWidth={effectiveCellWidth}
              filteredEvents={filteredEvents}
            />
            <CalendarBody
              slots={slots}
              days={days}
              daysToShow={daysToShow}
              cellWidth={effectiveCellWidth}
              filteredEvents={filteredEvents}
              onEventClick={onEventClick}
            />
          </div>
        </ScrollArea>
      </div>
    </div>
  );
};

export default CalendarViewContent;
